import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FindAllOrganizationLabelsPayload,
  OrganizationLabelPayload,
  OrganizationLabelsService,
} from "@/services/organizationLabels.service";

const ORGANIZATION_LABELS_LIST_KEY = "pret-organization-labels-list";
const ORGANIZATION_LABEL_BY_ID_KEY = "pret-organization-label-by-id";

export const useOrganizationLabels = (
  filters: FindAllOrganizationLabelsPayload
) => {
  const {
    data: organizationLabels,
    isLoading: isLoadingOrganizationLabels,
    isFetching: isFetchingOrganizationLabels,
    refetch: refetchOrganizationLabels,
  } = useQuery({
    queryKey: [ORGANIZATION_LABELS_LIST_KEY, filters],
    queryFn: () => OrganizationLabelsService.findAll(filters),
  });

  return {
    organizationLabels,
    isLoadingOrganizationLabels,
    isFetchingOrganizationLabels,
    refetchOrganizationLabels,
  };
};

export const useOrganizationLabel = (id: string, enabled = true) => {
  const {
    data: organizationLabel,
    isLoading: isLoadingOrganizationLabel,
    refetch: refetchOrganizationLabel,
  } = useQuery({
    queryKey: [ORGANIZATION_LABEL_BY_ID_KEY, id],
    queryFn: () => OrganizationLabelsService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    organizationLabel,
    isLoadingOrganizationLabel,
    refetchOrganizationLabel,
  };
};

export const useCreateOrganizationLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrganizationLabelPayload) =>
      OrganizationLabelsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_LABELS_LIST_KEY],
      });
    },
  });
};

export const useUpdateOrganizationLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: OrganizationLabelPayload;
    }) => OrganizationLabelsService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_LABELS_LIST_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_LABEL_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveOrganizationLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => OrganizationLabelsService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_LABELS_LIST_KEY],
      });
    },
  });
};
